/**
 * Response Parser Utilities
 *
 * Utilities for parsing and validating AI responses.
 */

/**
 * Parse JSON from AI response, handling markdown code blocks
 * @param {string} content - Response content that may contain JSON
 * @returns {Object} Parsed JSON object
 * @throws {Error} If JSON parsing fails
 */
export const parseJsonFromContent = content => {
  if (!content || typeof content !== 'string') {
    throw new Error('Invalid content: expected a string');
  }

  let jsonStr = content.trim();

  // Handle ```json ... ``` blocks
  const jsonBlockMatch = jsonStr.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonBlockMatch) {
    jsonStr = jsonBlockMatch[1].trim();
  }

  // Try to find JSON object or array
  const jsonMatch = jsonStr.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
  if (jsonMatch) {
    jsonStr = jsonMatch[1];
  }

  try {
    return JSON.parse(jsonStr);
  } catch (error) {
    throw new Error(`Failed to parse JSON from AI response: ${error.message}`);
  }
};

/**
 * Validate floor plan analysis response structure
 * @param {Object} response - Parsed response object
 * @returns {Object} Validated response with defaults applied
 * @throws {Error} If response structure is invalid
 */
export const validateFloorPlanResponse = response => {
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid response: expected an object');
  }

  // Ensure spaces array exists
  if (!Array.isArray(response.spaces)) {
    response.spaces = [];
  }

  // Validate and normalize each space
  response.spaces = response.spaces
    .filter(space => space && typeof space === 'object')
    .map((space, index) => ({
      temp_id: `space_${index}`,
      suggested_name: space.suggested_name || `Room ${index + 1}`,
      suggested_name_he: space.suggested_name_he || space.suggested_name || `חדר ${index + 1}`,
      suggested_type: space.suggested_type || 'Other',
      master_space_id: space.master_space_id || null,
      estimated_size_sqm: parseFloat(space.estimated_size_sqm) || 0,
      confidence: Math.min(1, Math.max(0, parseFloat(space.confidence) || 0.5)),
      status: 'pending', // pending | accepted | modified | rejected
    }));

  // Ensure metadata exists
  if (!response.metadata || typeof response.metadata !== 'object') {
    response.metadata = {};
  }

  response.metadata = {
    total_detected: response.spaces.length,
    scale_detected: response.metadata.scale_detected || null,
    notes: response.metadata.notes || '',
  };

  return response;
};

/**
 * Match detected spaces with master space types
 * @param {Array} detectedSpaces - Spaces detected by AI
 * @param {Array} masterSpaces - Master space types from database
 * @returns {Array} Spaces with matched master_space_id
 */
export const matchSpacesToMasterTypes = (detectedSpaces, masterSpaces) => {
  if (!Array.isArray(detectedSpaces) || !Array.isArray(masterSpaces)) {
    return detectedSpaces || [];
  }

  const masterSpaceMap = {};
  masterSpaces.forEach(ms => {
    // Map by name (case-insensitive)
    masterSpaceMap[ms.name?.toLowerCase()] = ms.id;
    // Also map Hebrew names if available
    if (ms.name_he) {
      masterSpaceMap[ms.name_he] = ms.id;
    }
  });

  return detectedSpaces.map(space => {
    // Try to find matching master space
    const type = space.suggested_type?.toLowerCase();
    const nameHe = space.suggested_name_he;

    let matchedId = null;

    // First try exact type match
    if (type && masterSpaceMap[type]) {
      matchedId = masterSpaceMap[type];
    }
    // Then try Hebrew name
    else if (nameHe && masterSpaceMap[nameHe]) {
      matchedId = masterSpaceMap[nameHe];
    }
    // Then try partial matching
    else if (type) {
      const partialMatch = Object.keys(masterSpaceMap).find(
        key => key.includes(type) || type.includes(key),
      );
      if (partialMatch) {
        matchedId = masterSpaceMap[partialMatch];
      }
    }

    return {
      ...space,
      master_space_id: matchedId || space.master_space_id,
    };
  });
};

/**
 * Compare detected spaces with existing project spaces
 * @param {Array} detectedSpaces - Newly detected spaces
 * @param {Array} existingSpaces - Existing project spaces
 * @returns {Object} Comparison results
 */
export const compareSpaces = (detectedSpaces, existingSpaces) => {
  const result = {
    newSpaces: [],
    missingSpaces: [],
    sizeChanges: [],
    matches: [],
  };

  if (!Array.isArray(detectedSpaces) || !Array.isArray(existingSpaces)) {
    return result;
  }

  const matchedExistingIds = new Set();
  const matchedDetectedIndices = new Set();

  // Find matches based on space type
  detectedSpaces.forEach((detected, detectedIndex) => {
    const potentialMatches = existingSpaces.filter(existing => {
      if (matchedExistingIds.has(existing.id)) return false;

      // Match by master_space_id or similar names
      const typeMatch = detected.master_space_id === existing.master_space_id;
      const nameMatch =
        detected.suggested_name?.toLowerCase() === existing.custom_space_name?.toLowerCase() ||
        detected.suggested_name_he === existing.custom_space_name;

      return typeMatch || nameMatch;
    });

    if (potentialMatches.length > 0) {
      // Take the best match (prefer type match, then name)
      const match = potentialMatches[0];
      matchedExistingIds.add(match.id);
      matchedDetectedIndices.add(detectedIndex);

      // Check for size differences
      const detectedSize = detected.estimated_size_sqm || 0;
      const existingSize = match.room_size || 0;
      const sizeDiff = detectedSize - existingSize; // Signed difference
      if (Math.abs(sizeDiff) > 0.5) {
        // More than 0.5 sqm difference
        result.sizeChanges.push({
          existingId: match.id,
          existingName: match.custom_space_name,
          existingSize: existingSize,
          detectedSize: detectedSize,
          sizeDifference: sizeDiff, // Signed: positive = larger, negative = smaller
          detectedLength: detected.room_length,
          detectedWidth: detected.room_width,
          detectedIndex,
        });
      }

      result.matches.push({
        existingId: match.id,
        detectedIndex,
        confidence: detected.confidence,
        reason: 'Type and name match',
      });
    }
  });

  // New spaces (detected but not matched to existing)
  result.newSpaces = detectedSpaces.filter((_, index) => !matchedDetectedIndices.has(index));

  // Missing spaces (existing but not matched to detected)
  result.missingSpaces = existingSpaces.filter(existing => !matchedExistingIds.has(existing.id));

  return result;
};
